import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Trash2 } from 'lucide-react'
import type { BudgetSheetState } from '../../../../../../services/projectAutofillDraftService.ts'
import {
  addBudgetSheetRow,
  addCustomBudgetColumn,
  applyBudgetSheetCellChange,
  columnHeaderLabel,
  countCustomBudgetColumns,
  defaultBudgetColumnCaptionEs,
  isBudgetSheetTemplateSupported,
  isCustomBudgetColumnId,
  MAX_BUDGET_DISPLAY_LABEL,
  MAX_BUDGET_SHEET_ROWS,
  MAX_CUSTOM_COLUMNS,
  removeBudgetSheetRow,
  removeCustomBudgetColumn,
  resetBudgetFromSnapshot,
  sumBudgetColumnTotal,
  updateBudgetColumnDisplayLabel,
  type BudgetSheetReadonlyReason
} from '../../../../../../utils/conversationalMode/budgetSheetStateUtils.ts'
import styles from './BudgetSheetEditor.module.scss'

const BUDGET_SHEET_TOAST_SHOW_DEBOUNCE_MS = 900
const BUDGET_SHEET_TOAST_VISIBLE_MS = 2200

function cellToInputString(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'number') return String(v)
  return String(v)
}

function isNumericCol(id: string): boolean {
  return id === 'quantity' || id === 'unitPrice' || id === 'total'
}

function colWidthClassName(id: string): string {
  switch (id) {
    case 'item':
      return styles.colItem
    case 'unit':
      return styles.colUnit
    case 'quantity':
      return styles.colQuantity
    case 'unitPrice':
      return styles.colUnitPrice
    case 'total':
      return styles.colTotal
    case 'notes':
      return styles.colNotes
    default:
      return isCustomBudgetColumnId(id) ? styles.colCustom : styles.colFlex
  }
}

type ItemTextareaProps = {
  className: string
  value: string
  disabled: boolean
  onChange: (v: string) => void
}

const BudgetSheetItemTextarea: React.FC<ItemTextareaProps> = ({ className, value, disabled, onChange }) => {
  const ref = useRef<HTMLTextAreaElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    let rafId = 0
    const apply = () => {
      const node = ref.current
      if (!node) return
      node.style.height = 'auto'
      node.style.height = `${node.scrollHeight}px`
    }
    const schedule = () => {
      if (rafId !== 0) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        rafId = 0
        apply()
      })
    }
    schedule()
    const ro = new ResizeObserver(schedule)
    const par = el.parentElement
    if (par) ro.observe(par)
    return () => {
      ro.disconnect()
      if (rafId !== 0) cancelAnimationFrame(rafId)
    }
  }, [value])
  return (
    <textarea
      ref={ref}
      rows={1}
      className={className}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

export type { BudgetSheetReadonlyReason }

type Props = {
  state: BudgetSheetState
  onChange: (next: BudgetSheetState) => void
  disabled: boolean
  readonlyReason: BudgetSheetReadonlyReason
  showHeading?: boolean
  flushLayout?: boolean
  dockInModal?: boolean
}

const READONLY_MESSAGES: Record<Exclude<BudgetSheetReadonlyReason, 'none'>, string> = {
  version:
    'Esta planilla usa una versión de plantilla que esta aplicación aún no puede editar. Podés verla pero no modificarla.',
  budget_slot:
    'Al publicar se usará el archivo o las páginas elegidas para el presupuesto. La planilla queda como referencia (solo lectura).',
  no_skill: 'Elegí el rubro de la lista para poder editar la planilla de cotización.',
  no_metric:
    'Completá el cómputo métrico del rubro antes de cargar o editar la planilla de cotización.',
  workflow_locked:
    'Generá las planillas de cotización después de revisar el cómputo (botón Continuar).',
  legajo_pdf_only:
    'Este rubro usa el presupuesto del PDF adjunto. La planilla editable se gestiona en la documentación.'
}

const BudgetSheetEditor: React.FC<Props> = ({
  state,
  onChange,
  disabled,
  readonlyReason,
  showHeading = true,
  flushLayout = false,
  dockInModal = false
}) => {
  const [newCustomLabel, setNewCustomLabel] = useState('')
  const [draftAppliedVisible, setDraftAppliedVisible] = useState(false)
  const showDraftToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hideDraftToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const draftToastActiveRef = useRef(false)
  const supported = isBudgetSheetTemplateSupported(state)
  const readOnly = disabled || readonlyReason !== 'none' || !supported
  const bannerText =
    readonlyReason !== 'none' ? READONLY_MESSAGES[readonlyReason] : !supported ? READONLY_MESSAGES.version : null

  const commitChange = useCallback(
    (next: BudgetSheetState) => {
      onChange(next)
      if (!dockInModal || readOnly) return

      if (showDraftToastTimerRef.current) {
        clearTimeout(showDraftToastTimerRef.current)
        showDraftToastTimerRef.current = null
      }

      const armHideDraftToast = () => {
        if (hideDraftToastTimerRef.current) clearTimeout(hideDraftToastTimerRef.current)
        hideDraftToastTimerRef.current = setTimeout(() => {
          draftToastActiveRef.current = false
          setDraftAppliedVisible(false)
          hideDraftToastTimerRef.current = null
        }, BUDGET_SHEET_TOAST_VISIBLE_MS)
      }

      if (draftToastActiveRef.current) {
        armHideDraftToast()
        return
      }

      showDraftToastTimerRef.current = setTimeout(() => {
        showDraftToastTimerRef.current = null
        draftToastActiveRef.current = true
        setDraftAppliedVisible(true)
        armHideDraftToast()
      }, BUDGET_SHEET_TOAST_SHOW_DEBOUNCE_MS)
    },
    [onChange, dockInModal, readOnly]
  )

  useEffect(() => {
    return () => {
      if (showDraftToastTimerRef.current) clearTimeout(showDraftToastTimerRef.current)
      if (hideDraftToastTimerRef.current) clearTimeout(hideDraftToastTimerRef.current)
      draftToastActiveRef.current = false
    }
  }, [])

  const hasTotalCol = state.columns.some((c) => c.id === 'total')
  const totalSum = useMemo(
    () => (hasTotalCol ? sumBudgetColumnTotal(state.rows) : 0),
    [hasTotalCol, state.rows]
  )

  const canAddRow = !readOnly && state.rows.length < MAX_BUDGET_SHEET_ROWS
  const canReset = !readOnly && !!state.suggestedSnapshot
  const customCount = countCustomBudgetColumns(state.columns)
  const canAddCustom =
    !readOnly && customCount < MAX_CUSTOM_COLUMNS && newCustomLabel.trim().length > 0

  const patchCell = (rowId: string, colId: string, raw: string) => {
    commitChange(applyBudgetSheetCellChange(state, rowId, colId, raw))
  }

  const handleAddRow = () => {
    const next = addBudgetSheetRow(state)
    if (next) commitChange(next)
  }

  const handleRemoveRow = (rowId: string) => {
    commitChange(removeBudgetSheetRow(state, rowId))
  }

  const handleReset = () => {
    const next = resetBudgetFromSnapshot(state)
    if (next) commitChange(next)
  }

  const handleAddCustomColumn = () => {
    const next = addCustomBudgetColumn(state, newCustomLabel)
    if (next) {
      commitChange(next)
      setNewCustomLabel('')
    }
  }

  const renderCellInput = (rowId: string, colId: string, className: string) => {
    const row = state.rows.find((r) => r.rowId === rowId)
    const raw = cellToInputString(row?.cells?.[colId])
    if (colId === 'item') {
      return (
        <BudgetSheetItemTextarea
          className={`${className} ${styles.inputItem}`}
          value={raw}
          disabled={readOnly}
          onChange={(v) => patchCell(rowId, colId, v)}
        />
      )
    }
    if (colId === 'notes') {
      return (
        <BudgetSheetItemTextarea
          className={`${className} ${styles.inputTextarea} ${styles.inputNotes}`}
          value={raw}
          disabled={readOnly}
          onChange={(v) => patchCell(rowId, colId, v)}
        />
      )
    }
    return (
      <input
        type="text"
        className={className}
        value={raw}
        disabled={readOnly}
        inputMode={isNumericCol(colId) ? 'decimal' : undefined}
        onChange={(e) => patchCell(rowId, colId, e.target.value)}
      />
    )
  }

  const thClassForCol = (colId: string) => {
    const base = isNumericCol(colId) ? styles.thNum : styles.th
    if (colId === 'item') return `${base} ${styles.thTall}`
    if (colId === 'notes') return `${base} ${styles.thTall}`
    return `${base} ${styles.thNarrow}`
  }

  const tdClassForCol = (colId: string) => {
    const base = isNumericCol(colId) ? styles.tdNum : styles.td
    if (colId === 'item') return `${base} ${styles.tdTall}`
    if (colId === 'notes') return `${base} ${styles.tdTall}`
    return `${base} ${styles.tdNarrow}`
  }

  const colIndex = (id: string) => state.columns.findIndex((c) => c.id === id)
  const hasCol = (id: string) => colIndex(id) >= 0

  return (
    <div
      className={[
        styles.wrap,
        !showHeading ? styles.wrapNoHeading : '',
        flushLayout ? styles.wrapFlush : '',
        dockInModal ? styles.wrapDockInModal : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {showHeading && <h4 className={styles.title}>Planilla de cotización</h4>}
      {bannerText && (
        <div className={[styles.banner, dockInModal ? styles.bannerDocked : ''].filter(Boolean).join(' ')}>
          {bannerText}
        </div>
      )}
      <div className={[styles.sheetFrame, dockInModal ? styles.sheetFrameDocked : ''].filter(Boolean).join(' ')}>
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            <button type="button" className={styles.toolBtn} disabled={!canAddRow} onClick={handleAddRow}>
              Agregar fila
            </button>
            <button type="button" className={styles.toolBtn} disabled={!canReset} onClick={handleReset}>
              Restablecer sugerencia
            </button>
          </div>
          {customCount < MAX_CUSTOM_COLUMNS ? (
            <div className={styles.toolbarRight}>
              <span className={styles.toolbarAddLabel}>Columna extra</span>
              <input
                type="text"
                className={styles.addCustomInput}
                value={newCustomLabel}
                maxLength={MAX_BUDGET_DISPLAY_LABEL}
                placeholder="Nombre"
                disabled={readOnly}
                title={`Podés sumar hasta ${MAX_CUSTOM_COLUMNS} columnas propias además de las de la planilla.`}
                aria-label="Nombre de la nueva columna personalizada"
                onChange={(e) => setNewCustomLabel(e.target.value)}
              />
              <button
                type="button"
                className={styles.toolBtn}
                disabled={!canAddCustom}
                onClick={handleAddCustomColumn}
              >
                Agregar columna
              </button>
            </div>
          ) : null}
        </div>

        <div
          className={[styles.desktopTable, dockInModal ? styles.desktopTableDocked : ''].filter(Boolean).join(' ')}
        >
          <div
            className={[styles.tableScroll, dockInModal ? styles.tableScrollDocked : ''].filter(Boolean).join(' ')}
          >
            <table className={`${styles.table} ${styles.tableFixed}`}>
              <colgroup>
                <col className={styles.colRowNum} />
                {state.columns.map((col) => (
                  <col key={col.id} className={colWidthClassName(col.id)} />
                ))}
                <col className={styles.colActions} />
              </colgroup>
              <thead className={dockInModal && !readOnly ? styles.theadInteractive : undefined}>
                <tr>
                  <th className={styles.thRowNum} scope="col">
                    N.º
                  </th>
                  {state.columns.map((col) => (
                    <th key={col.id} className={thClassForCol(col.id)} scope="col">
                      <div className={styles.thCell}>
                        <input
                          type="text"
                          className={styles.thLabelInput}
                          value={col.displayLabel ?? ''}
                          maxLength={MAX_BUDGET_DISPLAY_LABEL}
                          placeholder={defaultBudgetColumnCaptionEs(col.id)}
                          disabled={readOnly}
                          title="Renombrar columna"
                          aria-label={`Título de la columna: ${columnHeaderLabel(col)}`}
                          onChange={(e) =>
                            commitChange(updateBudgetColumnDisplayLabel(state, col.id, e.target.value))
                          }
                        />
                        {isCustomBudgetColumnId(col.id) ? (
                          <button
                            type="button"
                            className={styles.thRemoveCol}
                            disabled={readOnly}
                            onClick={() => commitChange(removeCustomBudgetColumn(state, col.id))}
                          >
                            Quitar columna
                          </button>
                        ) : null}
                      </div>
                    </th>
                  ))}
                  <th className={styles.thActions} aria-label="Acciones por fila" scope="col" />
                </tr>
              </thead>
              <tbody>
                {state.rows.length === 0 && (
                  <tr>
                    <td colSpan={state.columns.length + 2} className={styles.tdEmpty}>
                      <span className={styles.emptyHint}>
                        Todavía no hay filas en la planilla. Usá "Agregar fila".
                      </span>
                    </td>
                  </tr>
                )}
                {state.rows.map((row, idx) => (
                  <tr key={row.rowId}>
                    <td className={styles.tdRowNum}>{idx + 1}</td>
                    {state.columns.map((col) => (
                      <td key={col.id} className={tdClassForCol(col.id)}>
                        {renderCellInput(
                          row.rowId,
                          col.id,
                          isNumericCol(col.id) ? styles.inputNum : styles.input
                        )}
                      </td>
                    ))}
                    <td className={styles.rowActions}>
                      <button
                        type="button"
                        className={styles.removeRow}
                        disabled={readOnly}
                        title="Quitar esta fila"
                        aria-label="Quitar esta fila"
                        onClick={() => handleRemoveRow(row.rowId)}
                      >
                        <Trash2 size={18} strokeWidth={2} aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {hasTotalCol && state.rows.length > 0 && (
                <tfoot>
                  <tr className={styles.footerRow}>
                    <td />
                    {state.columns.map((col) => (
                      <td key={col.id} className={isNumericCol(col.id) ? styles.tdNum : styles.td}>
                        {col.id === 'total' ? (
                          <span className={styles.footerTotalValue}>
                            {Number.isFinite(totalSum)
                              ? totalSum.toLocaleString('es-AR', { maximumFractionDigits: 2 })
                              : ''}
                          </span>
                        ) : col.id === 'item' ? (
                          'Total'
                        ) : (
                          ''
                        )}
                      </td>
                    ))}
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        <div className={[styles.mobileList, dockInModal ? styles.mobileListDocked : ''].filter(Boolean).join(' ')}>
          {state.rows.length === 0 && (
            <p className={styles.emptyHint}>Todavía no hay filas en la planilla. Usá "Agregar fila".</p>
          )}
          {state.rows.map((row, idx) => (
            <div key={row.rowId} className={styles.card}>
              <div className={styles.cardRowNum}>Fila {idx + 1}</div>
              {hasCol('item') && (
                <>
                  <div className={styles.cardItemLabel}>
                    {columnHeaderLabel(state.columns[colIndex('item')]!)}
                  </div>
                  <BudgetSheetItemTextarea
                    className={`${styles.cardItemInput} ${styles.inputItem}`}
                    value={cellToInputString(row.cells?.item)}
                    disabled={readOnly}
                    onChange={(v) => patchCell(row.rowId, 'item', v)}
                  />
                </>
              )}
              <div className={styles.grid2}>
                {hasCol('unit') && (
                  <div>
                    <label className={styles.fieldLabel}>
                      {columnHeaderLabel(state.columns[colIndex('unit')]!)}
                    </label>
                    {renderCellInput(row.rowId, 'unit', styles.cardItemInput)}
                  </div>
                )}
                {hasCol('quantity') && (
                  <div>
                    <label className={styles.fieldLabel}>
                      {columnHeaderLabel(state.columns[colIndex('quantity')]!)}
                    </label>
                    {renderCellInput(row.rowId, 'quantity', styles.cardItemInput)}
                  </div>
                )}
                {hasCol('unitPrice') && (
                  <div>
                    <label className={styles.fieldLabel}>
                      {columnHeaderLabel(state.columns[colIndex('unitPrice')]!)}
                    </label>
                    {renderCellInput(row.rowId, 'unitPrice', styles.cardItemInput)}
                  </div>
                )}
                {hasCol('total') && (
                  <div>
                    <label className={styles.fieldLabel}>
                      {columnHeaderLabel(state.columns[colIndex('total')]!)}
                    </label>
                    {renderCellInput(row.rowId, 'total', styles.cardItemInput)}
                  </div>
                )}
              </div>
              {state.columns
                .filter((c) => !['item', 'unit', 'quantity', 'unitPrice', 'total'].includes(c.id))
                .map((col) => (
                  <div key={col.id}>
                    <label className={styles.fieldLabel}>{columnHeaderLabel(col)}</label>
                    {renderCellInput(row.rowId, col.id, styles.cardItemInput)}
                  </div>
                ))}
              <div className={styles.cardFooter}>
                <button
                  type="button"
                  className={styles.cardRemove}
                  disabled={readOnly}
                  onClick={() => handleRemoveRow(row.rowId)}
                >
                  Quitar fila
                </button>
              </div>
            </div>
          ))}
          {hasTotalCol && state.rows.length > 0 && (
            <div className={styles.card} style={{ background: '#f1f5f9' }}>
              <div className={styles.fieldLabel}>Total</div>
              <div className={styles.mobileTotalSum}>
                {Number.isFinite(totalSum) ? totalSum.toLocaleString('es-AR', { maximumFractionDigits: 2 }) : ''}
              </div>
            </div>
          )}
        </div>
        {dockInModal && !readOnly && draftAppliedVisible ? (
          <div className={styles.draftAppliedToast} role="status" aria-live="polite">
            Cambios aplicados al borrador
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default BudgetSheetEditor
